import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../config/queryKeys'
import { getUserSuspensions } from '../services/user/userSuspensions'

/**
 * Represents an enforcement type for a suspension
 */
export interface Enforcement {
  id: number
  name: string
  name_full: string
  deleted_at: string | null
}

/**
 * Represents an image format
 */
export interface ImageFormat {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
}

/**
 * Represents an image with its formats
 */
export interface Image {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
  formats: ImageFormat[]
}

/**
 * Represents a player associated with a suspension
 */
export interface Player {
  id: string
  name_first: string
  name_middle: string | null
  name_last: string
  full_name: string | null
  birth_date: string
  height: number | null
  weight: number | null
  created_at: string
  gender: string | null
  image: Image | null
  invitation_id: string | null
  player_number: string | null
  play_direction: string | null
  is_affiliate: boolean
  is_injured: boolean
  is_playing: boolean
  is_starting: boolean
  is_proposed_starting: boolean
  is_suspended: boolean
  attendance_status: string | null
  customer_identifier: string
  sf_id: string | null
  suspensions: any[]
  email: string
  phone_number: string
  is_accepted: boolean
  is_invited: boolean
  is_validated: boolean
  is_signed_waiver: boolean
  league_registration_number: string | null
  player_type: string | null
}

/**
 * Represents a team associated with a suspension
 */
export interface Team {
  id: string
  name: string
  name_full: string
  abbreviation: string | null
  email: string | null
  website: string | null
  phone: string | null
  is_public: boolean
  is_placeholder: boolean
  is_visible: boolean
  updated_at: string
  players_count: number
}

/**
 * Represents a period in a game
 */
export interface Period {
  id: string
  started_at: string
  duration: number
  ended_at: string | null
  game_id: string
  clock_time: string
  shots_home_count: number
  shots_visiting_count: number
}

/**
 * Represents a game associated with a suspension
 */
export interface Game {
  id: string
  sport_id: string
  generic_sport_name: string | null
  game_status_id: number
  game_type_id: number
  home_team_name: string
  visiting_team_name: string
  league_game_number: string
  starts_at: string
  ends_at: string
  started_at: string
  ended_at: string | null
  home_team_score: number
  visiting_team_score: number
  current_period: Period
  shootout: boolean
  venue_name: string
}

/**
 * Represents a schedule associated with a suspension
 */
export interface Schedule {
  uid: string
  name: string
  start_date: string
  end_date: string
}

/**
 * Represents a suspension
 */
export interface Suspension {
  id: string
  enforcement: Enforcement
  player: Player
  team: Team
  game_id: string
  game: Game
  schedule: Schedule
  start_date: string
  end_date: string
  timezone: string
  games_count: number | null
  games_remaining: number | null
  comment: string | null
  description: string
  is_active: boolean
  created_at: string
  updated_at: string
}

/**
 * Custom hook to fetch and manage user suspensions
 * @returns Object containing suspensions data, loading state, error state, and refetch function
 */
export const useUsersMeSuspensions = (onlyActive = false) => {
  const query = useQuery({
    queryKey: queryKeys.userSuspensions.me(onlyActive),
    queryFn: () => getUserSuspensions(onlyActive),
    staleTime: 1000 * 60 * 2,
  })

  const data = query.data ?? []

  const isSuspended = data.some(
    (suspension: Suspension) =>
      suspension.is_active && dayjs().isBefore(dayjs(suspension.end_date))
  )

  // `req` throws plain objects for 4xx API errors, not Error instances. Wrap any
  // non-Error throwable so consumers always get an Error on failure (never a
  // silently-null error), matching the previous hand-rolled behavior.
  const error: Error | null = query.error
    ? query.error instanceof Error
      ? query.error
      : new Error('An error occurred while fetching suspensions')
    : null

  return {
    data,
    // `isLoading` is the initial-load flag (no cached data yet) — it does not
    // flip on background refetches. Consumers that need a refresh spinner use
    // their own `refreshing` prop; this preserves the legacy `loading` meaning.
    loading: query.isLoading,
    error,
    isSuspended,
    refetch: query.refetch,
  }
}
